import axios from "axios";
import React from "react";
import { parseLastSeenTimestamp } from "utils/parseLastSeenTimestamp";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LockResponse, SiteSystemType } from "api/ApiClient";
import { isGatewayDeviceType } from "constants/deviceTypes";
import { isFirmwarePanelVisible } from "./lockDisplay";
import { nextDbDownload } from "./nextDbDownload";
import { useNow } from "shared/hooks/useNow";
import LockActivity from "./LockActivity";
import LockGuests from "./LockGuests";
import LockDatabaseHistory from "./LockDatabaseHistory";
import LockAutomation from "./LockAutomation";
import LockEvents from "./LockEvents";
import LockHolidays from "./LockHolidays";
import LockGroups from "./LockGroups";
import GatewayDetailView from "./components/GatewayDetailView";
import FirmwareContainer from "./components/ConnectedLockFirmware";
import LockDetails, { formatDoorType } from "./components/LockDetails";
import { ArrowLeft } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Badge } from "components/ui/badge";

async function getLock(lockId: string) {
  const result = await axios.get<LockResponse>(
    `${BASE_API_URL}/api/locks/${lockId}`,
  );
  return result.data;
}

function useGetLock(lockId: string) {
  return useQuery({
    queryKey: ["locks", "details", lockId],
    queryFn: () => getLock(lockId),
  });
}

export default function AdminLockPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams<{ lockId: string }>();
  const { data, isError, error } = useGetLock(params.lockId!);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const { mutate: deleteLock, isPending: isDeleting } = useMutation({
    mutationFn: () => axios.delete(`${BASE_API_URL}/api/Locks/${params.lockId}`),
    onSuccess: () => {
      setConfirmOpen(false);
      // Navigate away first so we don't refetch a now-deleted details query.
      navigate("/admin/locks", { replace: true });
      queryClient.invalidateQueries({ queryKey: ["locks"] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "An error occurred deleting the lock.";
      setDeleteError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  if (isError) {
    const status = (error as any)?.response?.status;
    if (status === 404) {
      return <Navigate to="/admin/locks" replace />;
    }
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/locks"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Locks
        </Link>
        <Alert variant="destructive">Unable to load lock. Please try again.</Alert>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/locks"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Locks
        </Link>
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
          Loading lock...
        </div>
      </main>
    );
  }

  return (
    <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
      <header className="pb-5 border-b border-slate-200">
        <Link
          to="/admin/locks"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Locks
        </Link>
      </header>
      {(isGatewayDeviceType(data.deviceType) || (data.deviceType === "cte" && data.operatingMode === "410ip")) && data.onlineStatus === "Offline" ? (
        <Alert variant="destructive">
          <div className="text-base font-semibold">Offline</div>
          <div className="mt-1 text-sm">
            {isGatewayDeviceType(data.deviceType) ? "Gateway is offline — live data unavailable." : "Lock is offline — connected gateway unreachable or lock out of range."}
          </div>
          {data.lastSeenTimeStamp && (
            <div className="mt-1 text-xs text-slate-500">
              Last online: {parseLastSeenTimestamp(data.lastSeenTimeStamp)?.toLocaleString() ?? "Unknown"}
            </div>
          )}
        </Alert>
      ) : null}
      <div className="grid gap-6 xl:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="px-1">
            <h1 className="text-2xl font-semibold text-slate-900">
              {data.name ?? "Lock"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              <Badge variant="secondary">{formatDoorType(data.doorType)}</Badge>
            </p>
          </div>
          <LockDetails
            lock={data}
            onDeleteClick={() => {
              setDeleteError(null);
              setConfirmOpen(true);
            }}
          />
          {isFirmwarePanelVisible(data) ? <FirmwarePanel lock={data} /> : null}
        </aside>
        <section className="min-w-0">
          {isGatewayDeviceType(data.deviceType) ? (
            <GatewayDetailView lock={data} />
          ) : (
            <>
              <Navigation
                siteSystemType={data.site?.account?.systemType}
                lockId={params.lockId!}
              />
              <div className="pt-4">
                <Routes>
                  {/* Nested, relative routes under :lockId/* */}
                  <Route
                    path="activity"
                    element={<LockActivity lockId={data.id!} />}
                  />
                  <Route
                    path="guests"
                    element={<LockGuests lockId={data.id!} />}
                  />
                  <Route
                    path="groups"
                    element={<LockGroups lockId={data.id!} />}
                  />
                  <Route
                    path="database-history"
                    element={<LockDatabaseHistory lockId={data.id!} />}
                  />
                  <Route
                    path="automations"
                    element={
                      <LockAutomation
                        lockId={data.id!}
                        siteTimeZone={data.site?.timeZone}
                      />
                    }
                  />
                  <Route
                    path="holidays"
                    element={
                      <LockHolidays
                        lockId={data.id!}
                        siteTimeZone={data.site?.timeZone}
                      />
                    }
                  />
                  <Route
                    path="events"
                    element={<LockEvents lockId={data.id!} />}
                  />
                  <Route
                    path="firmware"
                    element={<FirmwareContainer lock={data} />}
                  />
                  {/* Default redirect on initial load of /admin/locks/:lockId */}
                  <Route
                    index
                    element={
                      <Navigate
                        to={
                          data.site?.account?.systemType ===
                          SiteSystemType.SchlageEngage
                            ? "activity"
                            : "guests"
                        }
                        replace
                      />
                    }
                  />
                </Routes>
              </div>
            </>
          )}
        </section>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isDeleting) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="sm:max-w-xs"
          onEscapeKeyDown={(event) => {
            if (isDeleting) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (isDeleting) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Lock</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{data.name}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && <Alert variant="destructive">{deleteError}</Alert>}
          <DialogFooter>
            <Button
              disabled={isDeleting}
              onClick={() => setConfirmOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isDeleting}
              onClick={() => deleteLock()}
              type="button"
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function getNavigationLinks(siteSystemType: SiteSystemType | undefined) {
  const navLinks: { to: string; label: string }[] = [];

  if (siteSystemType === SiteSystemType.SchlageEngage) {
    navLinks.push({ to: "activity", label: "Activity" });
  }

  navLinks.push({ to: "guests", label: "Guests" });
  navLinks.push({ to: "groups", label: "Groups" });

  if (siteSystemType === SiteSystemType.SchlageEngage) {
    navLinks.push({ to: "database-history", label: "Database history" });
    navLinks.push({ to: "events", label: "Events" });
    navLinks.push({ to: "automations", label: "Automations" });
    navLinks.push({ to: "holidays", label: "Holidays" });
    navLinks.push({ to: "firmware", label: "Firmware" });
  }

  return navLinks;
}

function Navigation({
  siteSystemType,
  lockId,
}: {
  siteSystemType: SiteSystemType | undefined;
  lockId: string;
}) {
  const navLinks = getNavigationLinks(siteSystemType);
  const basePath = `/admin/locks/${lockId}`;
  const location = useLocation();
  const navigate = useNavigate();

  const activeTo =
    navLinks.find(({ to }) => {
      const path = `${basePath}/${to}`;
      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    })?.to ?? navLinks[0]?.to;

  return (
    <Tabs
      value={activeTo}
      onValueChange={(value) => navigate(`${basePath}/${value}`)}
    >
      <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
        {navLinks.map(({ to, label }) => (
          <TabsTrigger
            key={to}
            value={to}
            className="shrink-0 data-[state=active]:bg-slate-200!"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function FirmwarePanel({ lock }: { lock: LockResponse }) {
  // Ticks every minute so the Overdue badge appears on a page left open past the deadline.
  const now = useNow();
  const nextDownload = nextDbDownload(lock, now);
  const hasFirmware =
    lock.systemLockId ||
    lock.readerSerialNumber ||
    lock.deviceType ||
    lock.firmwareMain ||
    lock.firmwareLock ||
    lock.firmwareUpdateVersion ||
    lock.firmwareBLE2 ||
    lock.firmwareMainBl ||
    lock.firmwareMacAdr ||
    lock.firmwareEthernetMacAdr ||
    lock.configSyncTimestamp ||
    lock.auditSyncTimestamp ||
    lock.databaseSyncTimestamp ||
    nextDownload;

  return (
    <div className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-base font-semibold text-slate-900">Firmware &amp; Hardware</h3>
      </div>
      <div className="p-4">
        {hasFirmware ? (
          <table className="w-full text-sm text-left">
            <tbody className="divide-y divide-slate-100">
              {lock.systemLockId ? (
                <tr>
                  <th className="w-2/5 py-2 pr-4 font-medium align-top text-slate-500">Serial Number / System Lock Id</th>
                  <td className="py-2 font-mono text-slate-800">{lock.systemLockId}</td>
                </tr>
              ) : null}
              {lock.readerSerialNumber ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Reader Serial Number</th>
                  <td className="py-2 font-mono text-slate-800">{lock.readerSerialNumber}</td>
                </tr>
              ) : null}
              {lock.deviceType ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Device Type</th>
                  <td className="py-2 font-mono uppercase text-slate-800">{lock.deviceType}</td>
                </tr>
              ) : null}
              {lock.deviceType === "cte" && lock.operatingMode ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Operating Mode</th>
                  <td className="py-2 font-mono text-slate-800">{lock.operatingMode}</td>
                </tr>
              ) : null}
              {lock.firmwareMain ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Main Firmware</th>
                  <td className="py-2 font-mono text-slate-800">{lock.firmwareMain}</td>
                </tr>
              ) : null}
              {lock.firmwareLock ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Lock Firmware</th>
                  <td className="py-2 font-mono text-slate-800">{lock.firmwareLock}</td>
                </tr>
              ) : null}
              {lock.firmwareUpdateVersion ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Pending Update</th>
                  <td className="py-2 font-mono text-amber-600">{lock.firmwareUpdateVersion}</td>
                </tr>
              ) : null}
              {lock.firmwareBLE2 ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">BLE Firmware</th>
                  <td className="py-2 font-mono text-slate-800">{lock.firmwareBLE2}</td>
                </tr>
              ) : null}
              {lock.firmwareMainBl ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Bootloader</th>
                  <td className="py-2 font-mono text-slate-800">{lock.firmwareMainBl}</td>
                </tr>
              ) : null}
              {lock.firmwareMacAdr ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">MAC Address</th>
                  <td className="py-2 font-mono text-slate-800">{lock.firmwareMacAdr}</td>
                </tr>
              ) : null}
              {lock.firmwareEthernetMacAdr ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Ethernet MAC</th>
                  <td className="py-2 font-mono text-slate-800">{lock.firmwareEthernetMacAdr}</td>
                </tr>
              ) : null}
              {lock.configSyncTimestamp ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Config Synced</th>
                  <td className="py-2 text-slate-800">{parseLastSeenTimestamp(lock.configSyncTimestamp)?.toLocaleString() ?? "—"}</td>
                </tr>
              ) : null}
              {lock.auditSyncTimestamp ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Audits Synced</th>
                  <td className="py-2 text-slate-800">{parseLastSeenTimestamp(lock.auditSyncTimestamp)?.toLocaleString() ?? "—"}</td>
                </tr>
              ) : null}
              {lock.databaseSyncTimestamp ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">DB Synced</th>
                  <td className="py-2 text-slate-800">{parseLastSeenTimestamp(lock.databaseSyncTimestamp)?.toLocaleString() ?? "—"}</td>
                </tr>
              ) : null}
              {nextDownload ? (
                <tr>
                  <th className="py-2 pr-4 font-medium align-top text-slate-500">Next DB Download</th>
                  <td className="py-2 text-slate-800">
                    {nextDownload.estimated ? "≈ " : ""}
                    {nextDownload.at.toLocaleString()}
                    {nextDownload.estimated ? (
                      <span className="ml-1 text-slate-500">(estimated, lock default)</span>
                    ) : null}
                    {nextDownload.overdue ? (
                      <Badge className="ml-2" variant="warning">Overdue</Badge>
                    ) : null}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500">No firmware data available yet.</p>
        )}
      </div>
    </div>
  );
}
